/**
 * "Which desk are you at?" — asked once per PC, where desk phones stay on their desks.
 *
 * The answer decides which handsets ring for this dispatcher, so it is asked before the
 * first call rather than discovered on it. The PC remembers it and says it again on every
 * load; the telephone menu changes it. Choosing a desk somebody else is at takes it — they
 * were there last, and the phone should ring for whoever is in the chair now — and the list
 * says who that is, so nobody takes a colleague's desk without knowing.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useTelephoneContext } from '../shell/telephoneContext';
import { describe } from '../settings/errors';
import { Banner, Button, Dialog, Sub } from '../ui';

export function DeskChoice() {
  const { t } = useTranslation();
  const { mine, askForDesk, chooseDesk } = useTelephoneContext();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (mine === null || !askForDesk) return null;

  async function choose(deskId: string | null) {
    setBusy(true);
    setError(null);

    try {
      await chooseDesk(deskId);
    } catch (failure) {
      setError(describe(failure, t, 'desks.couldNotChoose'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      title={t('desks.which')}
      // Closing without an answer is an answer: no desk phone, asked again next load.
      onClose={() => void choose(null)}
    >
      <div className="flex flex-col gap-3">
        <p className="m-0 text-ink-2">{t('desks.whichHelp')}</p>

        {error !== null && <Banner tone="danger">{error}</Banner>}

        <ul className="m-0 p-0 list-none flex flex-col gap-1.5">
          {mine.desks.map((desk) => (
            <li key={desk.id}>
              <Button
                variant="outline"
                className="w-full justify-between"
                disabled={busy}
                onClick={() => void choose(desk.id)}
              >
                <span>
                  {desk.name} <Sub>{desk.extensions.join(', ')}</Sub>
                </span>
                {desk.takenBy !== null && (
                  <Sub>{t('desks.takenBy', { name: desk.takenBy })}</Sub>
                )}
              </Button>
            </li>
          ))}
        </ul>

        <Button variant="quiet" disabled={busy} onClick={() => void choose(null)}>
          {t('desks.none')}
        </Button>
      </div>
    </Dialog>
  );
}
